import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  CalendarDays,
  Clock,
  CheckCircle,
  Loader2,
  Send,
  Sparkles,
  Hand,
  Eye,
  Droplets,
  Scissors,
  Heart,
  X,
  Plus,
  Star,
  Gift,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceCategories = [
  {
    id: "facials",
    name: "Facial Treatments",
    icon: Sparkles,
    services: [
      { name: "Classic Facial", price: "$7,000" },
      { name: "Deep Cleaning Facial", price: "$11,000" },
      { name: "Acne Facial", price: "$11,000" },
      { name: "LED Therapy Facial", price: "$10,000" },
      { name: "High Frequency Facial", price: "$9,000" },
      { name: "Nano Needling Facial", price: "$14,000" },
      { name: "Microdermabrasion Facial", price: "$15,000" },
      { name: "Mild Chemical Facial", price: "$12,000" },
      { name: "Back Facial", price: "$8,000" },
      { name: "Deep Cleaning Back Facial", price: "$10,000" },
      { name: "Brightening Back Facial", price: "$13,000" },
    ],
  },
  {
    id: "nails",
    name: "Nail Services",
    icon: Hand,
    services: [
      { name: "Gel-X Short", price: "$8,000" },
      { name: "Gel-X Medium", price: "$10,000" },
      { name: "Acrylic Nails Short", price: "$7,000 – $12,000" },
      { name: "Acrylic Nails Medium", price: "$10,000 – $15,000" },
      { name: "Acrylic Nails Long", price: "$12,000 – $18,000" },
      { name: "Soak Off", price: "$2,000" },
      { name: "Builder Gel on Toes", price: "$3,000" },
      { name: "Spa Manicure", price: "$5,000" },
      { name: "Gel Manicure", price: "$8,000" },
      { name: "Classic Pedicure", price: "$7,000" },
      { name: "Jelly Pedicure", price: "$10,000" },
      { name: "Luxury Spa Pedicure", price: "$12,000" },
    ],
  },
  {
    id: "lashes",
    name: "Lash Extensions",
    icon: Eye,
    services: [
      { name: "Hybrid", price: "$10,000" },
      { name: "Volume", price: "$13,000" },
      { name: "Classic", price: "$15,000" },
      { name: "Cluster Lash", price: "$3,000" },
      { name: "Refill", price: "$3,000" },
    ],
  },
  {
    id: "waxing",
    name: "Body Waxing",
    icon: Droplets,
    services: [
      { name: "Brow", price: "$1,500" },
      { name: "Upper Lip", price: "$2,000" },
      { name: "Full Face", price: "$7,000" },
      { name: "Underarm", price: "$3,500" },
      { name: "Half Leg", price: "$7,000" },
      { name: "Full Leg", price: "$11,000" },
      { name: "Bikini Line", price: "$4,000" },
      { name: "Brazilian", price: "$12,000" },
      { name: "Brazilian Hollywood", price: "$14,000" },
      { name: "Brazilian Butt Strip", price: "$5,000 – $7,000" },
      { name: "Brazilian Cheeks + Strip", price: "$10,000 – $15,000" },
    ],
  },
  {
    id: "massage",
    name: "Massage",
    icon: Heart,
    services: [
      { name: "Swedish Massage (40 min)", price: "$12,000" },
      { name: "Aromatherapy Massage (40 min)", price: "$13,000" },
      { name: "Back Massage (30 min)", price: "$7,000" },
      { name: "Head, Neck, Face & Scalp Massage (30 min)", price: "$8,000" },
      { name: "Neck & Shoulder Massage (30 min)", price: "$6,000" },
      { name: "Wood Therapy Massage (30 min)", price: "$8,000" },
      { name: "Wood Therapy Massage (60 min)", price: "$15,000" },
      { name: "Wood Therapy Massage (90 min)", price: "$20,000" },
      { name: "Wood Therapy + Lymphatic Drainage Luxury Package (100 min)", price: "$35,000" },
    ],
  },
  {
    id: "hair",
    name: "Hair Services",
    icon: Scissors,
    services: [
      { name: "Relaxer (Touch-up)", price: "$8,000 – $15,000" },
      { name: "Virgin Relaxer", price: "$12,000 – $20,000" },
      { name: "Keratin Treatment", price: "$30,000 – $45,000" },
      { name: "Deep Conditioning", price: "$6,000 – $15,000" },
      { name: "Protein Treatment", price: "$10,000 – $20,000" },
      { name: "Steam Treatment", price: "$10,000 – $20,000" },
      { name: "Hair Trim", price: "$2,500" },
      { name: "Full Color (Short Hair)", price: "$8,000 – $15,000" },
      { name: "Root Touch-up", price: "$5,000 – $10,000" },
      { name: "Full Color (Medium Hair)", price: "$15,000 – $30,000" },
      { name: "Full Color (Long / Thick Hair)", price: "$30,000 – $45,000" },
      { name: "Streak", price: "$15,000 – $25,000" },
      { name: "Toner (Blonde Ash Finish)", price: "$10,000" },
      { name: "Silk Press", price: "Contact for price" },
      { name: "Basic Ponytail", price: "$6,000 – $10,000" },
      { name: "Styled / High Ponytail (Glam)", price: "$8,000 – $15,000" },
      { name: "Short Locs Detox", price: "$8,000 – $12,000" },
      { name: "Medium Locs Detox", price: "$12,000 – $17,000" },
      { name: "Long Locs Detox", price: "$17,000 – $22,000" },
      { name: "Half Stitch-in Extensions", price: "$9,000 – $13,000" },
      { name: "Full Stitch-in Extensions", price: "$13,000 – $17,000" },
      { name: "Closure Stitch-in", price: "$22,000" },
      { name: "Frontal Stitch-in", price: "$19,000" },
      { name: "Stitch-in Removal", price: "$5,000" },
    ],
  },
];

const timeSlots = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM",
  "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM",
  "5:00 PM", "5:30 PM",
];

export const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const preselectedCategory = searchParams.get("service") || "";

  const [step, setStep] = useState(1);
  const [expandedCategory, setExpandedCategory] = useState(preselectedCategory);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    birthday_month: "",
    notes: "",
    referral_code: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const getServiceValue = (service) => `${service.name} — ${service.price}`;

  const toggleService = (service) => {
    const val = getServiceValue(service);
    setSelectedServices((prev) =>
      prev.includes(val)
        ? prev.filter((s) => s !== val)
        : [...prev, val]
    );
  };

  const removeService = (service) => {
    setSelectedServices((prev) => prev.filter((s) => s !== service));
  };

  const canProceedStep1 = selectedServices.length > 0;
  const canProceedStep2 = selectedDate && selectedTime;
  const canSubmit = form.name && form.phone;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        services: selectedServices,
        date: selectedDate ? selectedDate.toISOString().split("T")[0] : "",
        time: selectedTime,
        name: form.name,
        phone: form.phone,
        email: form.email,
        birthday_month: form.birthday_month || undefined,
        notes: form.notes,
        referral_code: form.referral_code || undefined,
      };
      const response = await axios.post(`${API}/bookings`, payload);
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success("Appointment booked!", {
          description: "We'll confirm your booking shortly.",
        });
      }
    } catch (error) {
      toast.error("Failed to book appointment", {
        description: "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetBooking = () => {
    setStep(1);
    setExpandedCategory("");
    setSelectedServices([]);
    setSelectedDate(undefined);
    setSelectedTime("");
    setForm({ name: "", phone: "", email: "", notes: "", referral_code: "" });
    setIsSubmitted(false);
  };

  const disabledDays = [{ before: new Date() }];

  if (isSubmitted) {
    return (
      <div className="pt-20">
        <section className="py-24 md:py-32 bg-[#050505]">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#121212] border border-[#262626] p-12 text-center"
              data-testid="booking-success"
            >
              <CheckCircle className="w-16 h-16 text-[#C5A059] mx-auto mb-6" />
              <h3 className="font-heading text-2xl md:text-3xl text-white mb-4">
                Booking Requested
              </h3>
              <div className="mb-4">
                {selectedServices.map((s, i) => (
                  <p key={i} className="font-body text-sm text-white">{s}</p>
                ))}
              </div>
              <p className="font-body text-[#A3A3A3] mb-2">
                {selectedDate?.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} at {selectedTime}
              </p>
              <p className="font-body text-sm text-[#A3A3A3] mb-8">
                We will contact you at <strong className="text-white">{form.phone}</strong> to confirm your appointment.
              </p>

              {/* Loyalty Punch Card Notice */}
              <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 p-4 mb-8 inline-block text-left">
                <div className="flex items-center gap-3">
                  <Star size={20} className="text-[#C5A059] flex-shrink-0" />
                  <div>
                    <p className="font-body text-sm text-white">Punch card updated!</p>
                    <p className="font-body text-xs text-[#A3A3A3]">
                      This booking earns you a loyalty punch. <Link to="/loyalty" className="text-[#C5A059] hover:underline">Check your progress</Link>
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={resetBooking}
                  data-testid="booking-another-btn"
                  className="text-[#C5A059] font-body text-sm tracking-wider uppercase hover:underline"
                >
                  Book Another Appointment
                </button>
                <Link to="/loyalty" className="text-[#A3A3A3] font-body text-sm tracking-wider uppercase hover:text-white">
                  View Punch Card
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
              Book Your Visit
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-4 mb-4">
              Schedule an<br />
              <span className="gold-gradient">Appointment</span>
            </h1>
            <p className="font-body text-sm text-[#A3A3A3]">
              Select one or more services, pick a date & time, and we'll confirm your booking.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Step Indicator */}
      <section className="py-6 bg-[#0A0A0A] border-b border-[#262626]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center gap-2 md:gap-8">
            {[
              { num: 1, label: "Select Services" },
              { num: 2, label: "Pick Date & Time" },
              { num: 3, label: "Your Details" },
            ].map((s) => (
              <div key={s.num} className="flex items-center gap-2 md:gap-3">
                <div
                  className={`w-8 h-8 flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                    step >= s.num
                      ? "bg-[#C5A059] text-[#050505]"
                      : "border border-[#262626] text-[#A3A3A3]"
                  }`}
                  data-testid={`step-indicator-${s.num}`}
                >
                  {step > s.num ? <CheckCircle size={16} /> : s.num}
                </div>
                <span className={`font-body text-xs tracking-wider uppercase hidden sm:block ${
                  step >= s.num ? "text-white" : "text-[#A3A3A3]"
                }`}>
                  {s.label}
                </span>
                {s.num < 3 && (
                  <div className={`w-8 md:w-16 h-px ${step > s.num ? "bg-[#C5A059]" : "bg-[#262626]"}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-12 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">

          {/* Step 1: Select Services (multi) */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
              <h2 className="font-heading text-xl text-white mb-6">Choose Your Services</h2>
              <p className="font-body text-sm text-[#A3A3A3] mb-6">
                Tap a category to see services. Select as many as you'd like.
              </p>

              {/* Selected Services Summary */}
              {selectedServices.length > 0 && (
                <div className="bg-[#121212] border border-[#C5A059]/30 p-4 mb-6" data-testid="selected-services-summary">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                      Selected ({selectedServices.length})
                    </span>
                    <button
                      onClick={() => setSelectedServices([])}
                      className="text-[#A3A3A3] text-xs hover:text-white transition-colors"
                      data-testid="clear-all-services"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedServices.map((service) => (
                      <div
                        key={service}
                        className="flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/30 px-3 py-1.5 text-xs"
                      >
                        <span className="text-white font-body">{service.split(" — ")[0]}</span>
                        <button
                          onClick={() => removeService(service)}
                          className="text-[#A3A3A3] hover:text-white"
                          data-testid={`remove-service`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Category Accordion */}
              <div className="space-y-3 mb-8">
                {serviceCategories.map((cat) => {
                  const isExpanded = expandedCategory === cat.id;
                  const selectedInCategory = selectedServices.filter((s) =>
                    cat.services.some((svc) => s === getServiceValue(svc))
                  ).length;

                  return (
                    <div key={cat.id} className="bento-card overflow-hidden">
                      <button
                        onClick={() => setExpandedCategory(isExpanded ? "" : cat.id)}
                        data-testid={`booking-category-${cat.id}`}
                        className={`w-full flex items-center justify-between p-4 transition-all duration-300 ${
                          isExpanded ? "bg-[#C5A059]/5" : ""
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <cat.icon className={`w-5 h-5 ${isExpanded ? "text-[#C5A059]" : "text-[#A3A3A3]"}`} />
                          <span className={`font-body text-sm tracking-wider uppercase ${isExpanded ? "text-white" : "text-[#A3A3A3]"}`}>
                            {cat.name}
                          </span>
                          {selectedInCategory > 0 && (
                            <span className="bg-[#C5A059] text-[#050505] text-xs font-semibold px-2 py-0.5 rounded-full">
                              {selectedInCategory}
                            </span>
                          )}
                        </div>
                        <Plus className={`w-4 h-4 text-[#A3A3A3] transition-transform duration-300 ${isExpanded ? "rotate-45" : ""}`} />
                      </button>

                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          className="border-t border-[#262626] p-4"
                        >
                          <div className="grid md:grid-cols-2 gap-2">
                            {cat.services.map((service) => {
                              const val = getServiceValue(service);
                              const isSelected = selectedServices.includes(val);
                              return (
                                <button
                                  key={service.name}
                                  onClick={() => toggleService(service)}
                                  data-testid="booking-service-option"
                                  className={`text-left p-3 border transition-all duration-300 flex items-start gap-3 ${
                                    isSelected
                                      ? "border-[#C5A059] bg-[#C5A059]/10"
                                      : "border-[#262626] hover:border-[#C5A059]/50"
                                  }`}
                                >
                                  <div className={`w-5 h-5 flex-shrink-0 border flex items-center justify-center transition-all mt-0.5 ${
                                    isSelected ? "border-[#C5A059] bg-[#C5A059]" : "border-[#A3A3A3]"
                                  }`}>
                                    {isSelected && <CheckCircle size={12} className="text-[#050505]" />}
                                  </div>
                                  <div className="flex flex-col">
                                    <span className={`font-body text-sm ${isSelected ? "text-white" : "text-[#A3A3A3]"}`}>{service.name}</span>
                                    <span className={`font-heading text-sm ${isSelected ? "text-[#C5A059]" : "text-[#C5A059]/70"}`}>{service.price}</span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => { setStep(2); window.scrollTo(0, 0); }}
                disabled={!canProceedStep1}
                data-testid="booking-next-step2"
                className="bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:brightness-110 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next: Pick Date & Time ({selectedServices.length} service{selectedServices.length !== 1 ? "s" : ""})
              </button>
            </motion.div>
          )}

          {/* Step 2: Date & Time */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
              <div className="mb-6">
                <button onClick={() => setStep(1)} className="text-[#C5A059] text-sm hover:underline mb-4 block">
                  &larr; Back to service selection
                </button>
                <div className="bg-[#121212] border border-[#262626] p-4">
                  <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] block mb-2">
                    Selected Services ({selectedServices.length})
                  </span>
                  {selectedServices.map((s, i) => (
                    <p key={i} className="font-body text-sm text-white">{s}</p>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Calendar */}
                <div>
                  <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                    <CalendarDays size={14} className="inline mr-2" />
                    Select a Date
                  </h3>
                  <div className="bg-[#121212] border border-[#262626] p-4 inline-block">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={disabledDays}
                      className="text-white"
                      data-testid="booking-calendar"
                    />
                  </div>
                </div>

                {/* Time Slots */}
                <div>
                  <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                    <Clock size={14} className="inline mr-2" />
                    Select a Time
                  </h3>
                  {selectedDate ? (
                    <div className="grid grid-cols-3 gap-2">
                      {timeSlots.map((time) => (
                        <button
                          key={time}
                          onClick={() => setSelectedTime(time)}
                          data-testid={`booking-time-${time.replace(/[: ]/g, '')}`}
                          className={`p-3 border text-center text-sm font-body transition-all duration-300 ${
                            selectedTime === time
                              ? "border-[#C5A059] bg-[#C5A059]/10 text-white"
                              : "border-[#262626] text-[#A3A3A3] hover:border-[#C5A059]/50 hover:text-white"
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="font-body text-sm text-[#A3A3A3]">Please select a date first</p>
                  )}
                </div>
              </div>

              <div className="mt-8">
                <button
                  onClick={() => { setStep(3); window.scrollTo(0, 0); }}
                  disabled={!canProceedStep2}
                  data-testid="booking-next-step3"
                  className="bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:brightness-110 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Next: Your Details
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Contact Details */}
          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
              <div className="mb-6">
                <button onClick={() => setStep(2)} className="text-[#C5A059] text-sm hover:underline mb-4 block">
                  &larr; Back to date & time
                </button>

                <div className="bg-[#121212] border border-[#262626] p-4 mb-6">
                  <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] block mb-2">
                    Booking Summary
                  </span>
                  {selectedServices.map((s, i) => (
                    <p key={i} className="font-body text-sm text-white">{s}</p>
                  ))}
                  <p className="font-body text-sm text-[#A3A3A3] mt-2">
                    {selectedDate?.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} at <strong className="text-white">{selectedTime}</strong>
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                  Your Information
                </h3>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Name *</label>
                    <input
                      type="text" name="name" value={form.name} onChange={handleFormChange} required
                      data-testid="booking-name" placeholder="Full name"
                      className="w-full luxury-input"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Phone *</label>
                    <input
                      type="tel" name="phone" value={form.phone} onChange={handleFormChange} required
                      data-testid="booking-phone" placeholder="Phone number"
                      className="w-full luxury-input"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Email</label>
                    <input
                      type="email" name="email" value={form.email} onChange={handleFormChange}
                      data-testid="booking-email" placeholder="Email (optional)"
                      className="w-full luxury-input"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">
                      Birthday Month
                    </label>
                    <select
                      name="birthday_month" value={form.birthday_month} onChange={handleFormChange}
                      data-testid="booking-birthday-month"
                      className="w-full bg-transparent border-b border-white/20 text-white py-3 focus:border-[#C5A059] focus:outline-none"
                      style={{ borderRadius: 0 }}
                    >
                      <option value="" className="bg-[#121212]">Select month (optional)</option>
                      {["January","February","March","April","May","June","July","August","September","October","November","December"].map(m => (
                        <option key={m} value={m} className="bg-[#121212]">{m}</option>
                      ))}
                    </select>
                    <p className="font-body text-[10px] text-[#A3A3A3] mt-1">Get a special 10% birthday discount during your birthday month!</p>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Notes</label>
                  <textarea
                    name="notes" value={form.notes} onChange={handleFormChange} rows={3}
                    data-testid="booking-notes" placeholder="Any special requests or notes..."
                    className="w-full luxury-input resize-none"
                  />
                </div>

                <div className="mb-8">
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">
                    <Gift size={12} className="inline mr-1 text-[#C5A059]" />
                    Referral Code (Optional)
                  </label>
                  <input
                    type="text" name="referral_code" value={form.referral_code} onChange={handleFormChange}
                    data-testid="booking-referral-code" placeholder="Enter a friend's referral code (e.g. REF-XXXXXX)"
                    className="w-full luxury-input uppercase"
                    maxLength={10}
                  />
                  <p className="font-body text-[10px] text-[#A3A3A3] mt-1">
                    Were you referred by a friend? Enter their code and you both earn a bonus punch!
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  data-testid="booking-submit-btn"
                  className="w-full bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase py-4 hover:brightness-110 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Booking...</>
                  ) : (
                    <><Send className="w-5 h-5" /> Confirm Booking ({selectedServices.length} service{selectedServices.length !== 1 ? "s" : ""})</>
                  )}
                </button>

                {/* MMG Payment Notice */}
                <div className="mt-6 bg-[#121212] border border-[#C5A059]/20 p-4 flex items-start gap-3" data-testid="mmg-payment-notice">
                  <Gift size={18} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-[#C5A059] text-xs font-semibold tracking-wider uppercase block mb-1">We Accept MMG Payments</span>
                    <p className="text-[#A3A3A3] text-xs leading-relaxed">
                      Pay conveniently through MMG. Merchant Name: <strong className="text-white">LB Beauty</strong>.
                      After payment, send your receipt to Luxury Beauty to confirm.
                    </p>
                  </div>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
